import React from 'react'
import cx from 'classnames'
import { useIntl } from 'intl'
import { useFt } from 'hooks'
import { constants } from 'helpers'

import { formatPrice } from 'helpers/modifiers'
import { useProductCollaborationLabel } from 'modules/collaborations'
import type { RebrandLabel, Label as LabelType } from 'typings/graphql'

import { Label } from 'components/dataDisplay'
import GraphicLabels from 'compositions/ProductGraphicLabels/ProductGraphicLabels'
import useExcludeLabels from 'compositions/productCards/components/Labels/util/useExcludeLabels'

import s from './Labels.scss'
import messages from './messages'


export type LabelsProps = {
  labels?: RebrandLabel
  excludeLabels?: LabelType[]
  upchargePrice?: number
  isEcommerce?: boolean
  isSmallCard?: boolean
}

const Labels: React.FunctionComponent<LabelsProps> = (props) => {
  const { labels: rebrandLabels, upchargePrice, isEcommerce, isSmallCard, excludeLabels } = props

  const intl = useIntl()
  const labels = useExcludeLabels(rebrandLabels, excludeLabels)
  let collaborationLabel = useProductCollaborationLabel({ labels: labels as ProductFragment.Base['rebrandLabels'] })
  const isPlusFreeGiftEnabled = useFt(constants.features.holiday22BrandedGWP)
  const holidayDiscount = useFt(constants.features.holiday22SaleBadge15) ? '15%' : '20%'

  const areExtraItemsExist = Boolean(labels?.extra?.length)
  const areMarketingItemsExist = Boolean(labels?.marketing?.length)

  if (!areExtraItemsExist && !areMarketingItemsExist && !collaborationLabel) {
    return null
  }

  const isHoliday = labels?.extra?.includes('HOLIDAY_SALE')
  const isPlusFreeGift = isPlusFreeGiftEnabled && labels?.extra?.includes('PLUS_FREE_GIFT')

  const mainLabels = []

  if (collaborationLabel && !isHoliday) {
    mainLabels.push({
      text: collaborationLabel,
      bgColor: 'black',
      textColor: 'gold-30',
    })
  }

  if (isHoliday) {
    mainLabels.push({
      text: intl.formatMessage(messages.holiday, { holidayDiscount }),
      bgColor: 'brown-red',
      textColor: 'white',
    })
  }
  else {
    const items = []

    if (upchargePrice && !isEcommerce) {
      items.push(`+${formatPrice(upchargePrice)}`)
    }

    if (labels?.extra?.includes('EXCLUSIVE')) {
      items.push(intl.formatMessage(messages.exclusive))
    }

    if (labels?.extra?.includes('FOR_HER')) {
      items.push(intl.formatMessage(messages.forHer))
    }

    if (labels?.extra?.includes('FOR_HIM')) {
      items.push(intl.formatMessage(messages.forHim))
    }

    if (items.length) {
      mainLabels.push({
        text: items.join(' • '),
        bgColor: 'black',
        textColor: 'gold-30',
      })
    }
  }

  if (isPlusFreeGift) {
    mainLabels.push({
      text: messages.plusFreeGift,
      bgColor: 'gold-70',
      textColor: 'white',
    })
  }

  const hasMainLabels = Boolean(mainLabels.length)
  const graphicLabelsClassName = hasMainLabels ? 'pt-8' : 'pt-16'

  return (
    <div className={cx(s.labels, 'absolute top-0 left-0 flex flex-col items-start')}>
      {
        mainLabels.map(({ text, textColor, bgColor }, index) => (
          <Label
            key={index}
            className={s.label}
            title={text}
            bgColor={bgColor}
            textColor={textColor}
            withTail
          />
        ))
      }
      {
        areMarketingItemsExist && (
          <GraphicLabels
            className={cx('pl-8', isSmallCard && s.small, graphicLabelsClassName)}
            items={labels.marketing}
            limit={isSmallCard ? 1 : 2}
          />
        )
      }
    </div>
  )
}


export default React.memo(Labels)
